import { MAX_RAYS, MAX_SPARKS, layerOf, type SparksLayer, type VfxDefinition } from "../../vfx/definition.ts";
import { AURA_STEPS, newSparkState, newVfxState, sparkState, type VfxState } from "../../vfx/state.ts";
import { GOLD_TONES, type Tones } from "../../vfx/tone.ts";
import { AURA_FRAME, MAX_RING, PIXEL_FRAME, rayFrame, ringFrame, sparkFrame } from "./art.ts";
import { centredLeft, goldHalf, pillarBox, rayDotsOf, splitPillar, type PillarSplit } from "./geometry.ts";
import { hideSprite, putSprite, type BurstSprite } from "./sprite.ts";

/**
 * Drawing one running vfx (ADR-134): the images of one slot, made once and reused, and the placing of each from the vfx's
 * state (`vfx/state.ts`), its tones and the art pixel's size. It decides nothing about the vfx; it puts what the state says
 * where the geometry says, in the colours the tones give.
 */

/**
 * The sign's fill, a dark brown, in 0xRRGGBB.
 */
export const SIGN_FILL = 0x3c2814;

const AURA_ALPHA = 0.45;
const BODY_PILLAR_ALPHA = 0.5;
const BODY_UP = 16;
const RAY_FRAMES = MAX_RAYS;
const AURA_HALF_WIDTH = 14;
const AURA_HALF_HEIGHT = 22;
const SIGN_PAD = 3;
const SIGN_BORDER = 1;
const SIGN_GAP = 4;
const RAY_SQUASH = 0.7;
const RING_SQUASH = 0.4;
const RING_FRAME_FROM = 2;

/**
 * The text of the sign: the part of a bitmap text the drawing sets.
 */
export interface SignText {
  readonly width: number;
  readonly height: number;
  setText(text: string): unknown;
  setTint(tint: number): unknown;
  setPosition(x: number, y: number): unknown;
  setVisible(visible: boolean): unknown;
}

/**
 * The images of one burst.
 */
export interface BurstSet {
  /** The vfx the slot runs, null before its first start. */
  def: VfxDefinition | null;
  /** The sparks layer of that vfx, null when it has none. */
  sparksLayer: SparksLayer | null;
  tones: Tones;
  /** The multiple of the art pixel's size this play asked for. */
  scale: number;
  startedAt: number;
  own: boolean;
  seen: number;
  over: boolean;
  reduced: boolean;
  elapsed: number;
  readonly state: VfxState;
  readonly ring: BurstSprite;
  readonly aura: BurstSprite;
  readonly pillarDark: BurstSprite;
  readonly pillarGold: BurstSprite;
  readonly pillarCore: BurstSprite;
  readonly bodyDark: BurstSprite;
  readonly bodyGold: BurstSprite;
  readonly bodyCore: BurstSprite;
  readonly rays: readonly BurstSprite[];
  readonly sparks: readonly BurstSprite[];
  readonly signBorder: BurstSprite;
  readonly signFill: BurstSprite;
  readonly text: SignText;
  textShown: boolean;
  /** How many rays and sparks the last draw showed. */
  raysShown: number;
  sparksShown: number;
}

/**
 * What a draw is given besides the burst: the UI scale, the art pixel's size in device pixels, the reach (the multiple of
 * the ring's radius and of the sparks' columns and rise, 1 for the definition's own size), and the spark state and the
 * pillar's split every burst is written into.
 */
export interface DrawContext {
  scale: number;
  k: number;
  reach: number;
  readonly spark: ReturnType<typeof newSparkState>;
  readonly split: PillarSplit;
}

/**
 * Where a burst is drawn: its character's feet in device pixels, and the device row the sign's foot stands over.
 */
export interface DrawAt {
  x: number;
  y: number;
  signStand: number;
  body: number;
}

/**
 * A context to write into.
 *
 * @returns A context of zeros.
 */
export function newDrawContext(): DrawContext {
  return { scale: 1, k: 1, reach: 1, spark: newSparkState(), split: { upperTop: 0, upperHeight: 0, bodyTop: 0, bodyHeight: 0 } };
}

/**
 * What makes each kind of image of a burst: each returns a hidden sprite in the UI layer at the depth its kind draws at.
 */
export interface SetFactories {
  readonly ring: () => BurstSprite;
  readonly aura: () => BurstSprite;
  readonly pillarDark: () => BurstSprite;
  readonly pillarGold: () => BurstSprite;
  readonly pillarCore: () => BurstSprite;
  readonly ray: () => BurstSprite;
  readonly spark: () => BurstSprite;
  readonly signBorder: () => BurstSprite;
  readonly signFill: () => BurstSprite;
}

/**
 * Makes the images of one burst.
 *
 * @param make Makes each kind of sprite.
 * @param text The sign's text, hidden and already set.
 * @returns The burst's images.
 */
export function createSet(make: SetFactories, text: SignText): BurstSet {
  return {
    def: null,
    sparksLayer: null,
    tones: GOLD_TONES,
    scale: 1,
    startedAt: 0,
    own: false,
    seen: 0,
    over: false,
    reduced: false,
    elapsed: 0,
    state: newVfxState(),
    ring: make.ring(),
    aura: make.aura(),
    pillarDark: make.pillarDark(),
    pillarGold: make.pillarGold(),
    pillarCore: make.pillarCore(),
    bodyDark: make.pillarDark(),
    bodyGold: make.pillarGold(),
    bodyCore: make.pillarCore(),
    rays: Array.from({ length: MAX_RAYS }, make.ray),
    sparks: Array.from({ length: MAX_SPARKS }, make.spark),
    signBorder: make.signBorder(),
    signFill: make.signFill(),
    text,
    textShown: false,
    raysShown: 0,
    sparksShown: 0,
  };
}

/**
 * Hides every image of a burst.
 *
 * @param set The burst.
 */
export function hideSet(set: BurstSet): void {
  hideSprite(set.ring);
  hideSprite(set.aura);
  hideSprite(set.pillarDark);
  hideSprite(set.pillarGold);
  hideSprite(set.pillarCore);
  hideSprite(set.bodyDark);
  hideSprite(set.bodyGold);
  hideSprite(set.bodyCore);
  hideSprite(set.signBorder);
  hideSprite(set.signFill);
  for (const ray of set.rays) {
    hideSprite(ray);
  }
  for (const spark of set.sparks) {
    hideSprite(spark);
  }
  if (set.textShown) {
    set.text.setVisible(false);
    set.textShown = false;
  }
  set.raysShown = 0;
  set.sparksShown = 0;
}

/**
 * Draws the ring: the dark one closing in the wind-up, the bright one spreading after, flat on the ground at the feet.
 *
 * @param set The burst.
 * @param at Where it stands.
 * @param context The draw context.
 */
function drawRing(set: BurstSet, at: DrawAt, context: DrawContext): void {
  const radius = Math.min(MAX_RING, Math.round(set.state.ringRadius * context.reach));
  if (radius < RING_FRAME_FROM) {
    hideSprite(set.ring);
    return;
  }
  const { k } = context;
  const half = Math.round(radius * RING_SQUASH);
  putSprite(set.ring, ringFrame(radius), centredLeft(at.x, radius, k), centredLeft(at.y, half, k), (2 * radius + 1) * k, (2 * half + 1) * k, context.scale, set.state.ringBright ? set.tones.mid : set.tones.dark, 1);
}

/**
 * Draws the aura over the body.
 *
 * @param set The burst.
 * @param at Where it stands.
 * @param context The draw context.
 */
function drawAura(set: BurstSet, at: DrawAt, context: DrawContext): void {
  const step = set.state.auraStep;
  if (step <= 0) {
    hideSprite(set.aura);
    return;
  }
  const { k } = context;
  putSprite(set.aura, AURA_FRAME, centredLeft(at.x, AURA_HALF_WIDTH, k), centredLeft(at.y - BODY_UP * k, AURA_HALF_HEIGHT, k), (2 * AURA_HALF_WIDTH + 1) * k, (2 * AURA_HALF_HEIGHT + 1) * k, context.scale, set.tones.light, (step / AURA_STEPS) * AURA_ALPHA);
}

/**
 * Draws one stretch of the pillar: a dark edge, the gold inside it and a white core.
 *
 * @param parts The stretch's three sprites, dark, mid and core.
 * @param tones The vfx's tones.
 * @param x The feet across, in device pixels.
 * @param half The pillar's half width in art pixels.
 * @param top The stretch's top row.
 * @param height The stretch's height; none is drawn at 0.
 * @param alpha The stretch's strength.
 * @param context The draw context.
 */
function drawStretch(parts: readonly [BurstSprite, BurstSprite, BurstSprite], tones: Tones, x: number, half: number, top: number, height: number, alpha: number, context: DrawContext): void {
  const [dark, gold, core] = parts;
  if (height <= 0) {
    hideSprite(dark);
    hideSprite(gold);
    hideSprite(core);
    return;
  }
  const { k, scale } = context;
  const inner = goldHalf(half);
  putSprite(dark, PIXEL_FRAME, centredLeft(x, half, k), top, (2 * half + 1) * k, height, scale, tones.dark, alpha);
  putSprite(gold, PIXEL_FRAME, centredLeft(x, inner, k), top, (2 * inner + 1) * k, height, scale, tones.mid, alpha);
  putSprite(core, PIXEL_FRAME, centredLeft(x, 1, k), top, 3 * k, height, scale, tones.core, alpha);
}

/**
 * Draws the pillar standing on the feet and rising to the top of the screen: solid above the body, and at half strength
 * over the body so the character shows through the light.
 *
 * @param set The burst.
 * @param at Where it stands.
 * @param context The draw context.
 */
function drawPillar(set: BurstSet, at: DrawAt, context: DrawContext): void {
  const half = set.state.pillarHalf;
  const box = pillarBox(at.x, at.y, half, set.state.pillarGrow, context.k);
  if (half === 0 || box.height === 0) {
    drawStretch([set.pillarDark, set.pillarGold, set.pillarCore], set.tones, at.x, 0, 0, 0, 1, context);
    drawStretch([set.bodyDark, set.bodyGold, set.bodyCore], set.tones, at.x, 0, 0, 0, 1, context);
    return;
  }
  const split = splitPillar(box, at.body, context.split);
  drawStretch([set.pillarDark, set.pillarGold, set.pillarCore], set.tones, at.x, half, split.upperTop, split.upperHeight, 1, context);
  drawStretch([set.bodyDark, set.bodyGold, set.bodyCore], set.tones, at.x, half, split.bodyTop, split.bodyHeight, BODY_PILLAR_ALPHA, context);
}

/**
 * Draws the rays: short lines of dots going out from the middle of the body, flattened as the ground is, spread evenly
 * over the twelve angles the atlas holds.
 *
 * @param set The burst.
 * @param at Where it stands.
 * @param context The draw context.
 */
function drawRays(set: BurstSet, at: DrawAt, context: DrawContext): void {
  const { rayFrom, rayTo, rayCount } = set.state;
  set.raysShown = 0;
  const stride = rayCount > 0 ? Math.max(1, Math.floor(RAY_FRAMES / rayCount)) : 1;
  const { k, scale } = context;
  const dots = rayDotsOf(rayTo - rayFrom);
  const reach = 2 * (dots - 1);
  const halfHeight = Math.ceil(reach * RAY_SQUASH);
  const middleY = at.y - BODY_UP * k;
  for (let index = 0; index < set.rays.length; index += 1) {
    const ray = set.rays[index] as BurstSprite;
    if (rayFrom === 0 || index >= rayCount) {
      hideSprite(ray);
      continue;
    }
    const angleIndex = index * stride;
    const angle = (angleIndex * 2 * Math.PI) / RAY_FRAMES;
    const x = at.x + Math.round(Math.cos(angle) * rayFrom) * k;
    const y = middleY + Math.round(Math.sin(angle) * rayFrom * RAY_SQUASH) * k;
    putSprite(ray, rayFrame(angleIndex, dots), centredLeft(x, reach, k), centredLeft(y, halfHeight, k), (2 * reach + 1) * k, (2 * halfHeight + 1) * k, scale, set.tones.light, 1);
    set.raysShown += 1;
  }
}

/**
 * Draws the sparks in flight.
 *
 * @param set The burst.
 * @param at Where it stands.
 * @param context The draw context.
 */
function drawSparks(set: BurstSet, at: DrawAt, context: DrawContext): void {
  const clock = set.state.sparkClock;
  const layer = set.sparksLayer;
  const { k, scale, spark, reach } = context;
  const middleY = at.y - BODY_UP * k;
  set.sparksShown = 0;
  for (let index = 0; index < set.sparks.length; index += 1) {
    const sprite = set.sparks[index] as BurstSprite;
    if (layer === null || clock < 0 || !sparkState(layer, index, clock, spark)) {
      hideSprite(sprite);
      continue;
    }
    putSprite(sprite, sparkFrame(spark.arm), centredLeft(at.x + Math.round(spark.dx * reach) * k, 2, k), centredLeft(middleY + Math.round(spark.dy * reach) * k, 2, k), 5 * k, 5 * k, scale, spark.light === 1 ? set.tones.light : set.tones.mid, 1);
    set.sparksShown += 1;
  }
}

/**
 * Draws the sign: a box with a dark-gold border and a dark fill, holding the words, centred over the character with its
 * foot a few pixels over the row the plate's stack ends at, risen by the state's rise. The box is sized in logical pixels
 * from the words, so the pixel font stays on its own grid.
 *
 * @param set The burst.
 * @param at Where it stands.
 * @param context The draw context.
 */
function drawSign(set: BurstSet, at: DrawAt, context: DrawContext): void {
  const rise = set.state.signRise;
  if (rise < 0) {
    hideSprite(set.signBorder);
    hideSprite(set.signFill);
    if (set.textShown) {
      set.text.setVisible(false);
      set.textShown = false;
    }
    return;
  }
  const { k, scale } = context;
  const boxWidth = (set.text.width + 2 * SIGN_PAD + 2 * SIGN_BORDER) * scale;
  const boxHeight = (set.text.height + 2 * SIGN_PAD + 2 * SIGN_BORDER) * scale;
  const left = at.x - Math.floor(boxWidth / 2);
  const top = at.signStand - SIGN_GAP * scale - boxHeight - rise * k;
  const border = SIGN_BORDER * scale;
  putSprite(set.signBorder, PIXEL_FRAME, left, top, boxWidth, boxHeight, scale, set.tones.dark, 1);
  putSprite(set.signFill, PIXEL_FRAME, left + border, top + border, boxWidth - 2 * border, boxHeight - 2 * border, scale, SIGN_FILL, 1);
  set.text.setPosition((left + border + SIGN_PAD * scale) / scale, (top + border + SIGN_PAD * scale) / scale);
  if (!set.textShown) {
    set.text.setVisible(true);
    set.textShown = true;
  }
}

/**
 * Draws a burst at its character's feet from its state.
 *
 * @param set The burst, its state written for this frame.
 * @param at Where its character's feet stand in device pixels and where the sign's foot stands over.
 * @param context The draw context, with the scale, the art pixel's size and the screen.
 */
export function drawSet(set: BurstSet, at: DrawAt, context: DrawContext): void {
  drawRing(set, at, context);
  drawAura(set, at, context);
  drawPillar(set, at, context);
  drawRays(set, at, context);
  drawSparks(set, at, context);
  drawSign(set, at, context);
}

/**
 * Points a slot at a vfx for a new start: the definition, its sparks layer, the tones and the first frame's state.
 *
 * @param set The slot's images.
 * @param definition The vfx.
 * @param tones The tones it is drawn in.
 */
export function bindSet(set: BurstSet, definition: VfxDefinition, tones: Tones): void {
  set.def = definition;
  set.sparksLayer = layerOf(definition, "sparks") ?? null;
  set.tones = tones;
}
